import React from 'react';
import { createPortal } from 'react-dom';
import { FileText, ShieldCheck, Receipt, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SAFFRON = '#E8A020';

const DOCS = [
  { icon: FileText, label: 'Policy Schedule', path: 'policy-schedule', testid: 'celebrate-download-schedule' },
  { icon: ShieldCheck, label: 'Certificate of Currency', path: 'certificate', testid: 'celebrate-download-certificate' },
  { icon: Receipt, label: 'Closing / Debit Note', path: 'closing-pack', testid: 'celebrate-download-closing' },
];

export function BindCelebration({ policyId, policyNumber, seconds, onClose }) {
  // Portalled to <body>: an animated (transformed) ancestor would otherwise clip the fixed backdrop to itself.
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto"
      style={{ background: 'rgba(13,27,42,0.96)', backdropFilter: 'blur(6px)' }} data-testid="bind-celebration">
      <style>{`
        @keyframes bc-circle { from { stroke-dashoffset: 290; } to { stroke-dashoffset: 0; } }
        @keyframes bc-check { from { stroke-dashoffset: 60; } to { stroke-dashoffset: 0; } }
        @keyframes bc-pop { 0% { transform: scale(0.7); opacity: 0; } 70% { transform: scale(1.04); } 100% { transform: scale(1); opacity: 1; } }
        .bc-card { animation: bc-pop 0.45s cubic-bezier(0.2,0.9,0.3,1.2) both; }
        .bc-circle { stroke-dasharray: 290; animation: bc-circle 0.9s ease-out 0.2s both; }
        .bc-check { stroke-dasharray: 60; animation: bc-check 0.45s ease-out 1.0s both; }
      `}</style>
      <div className="bc-card w-full max-w-xl mx-4 my-8 rounded-lg bg-white p-10 text-center" style={{ boxShadow: '0 30px 80px rgba(0,0,0,0.45)' }}>
        <svg width="96" height="96" viewBox="0 0 100 100" className="mx-auto">
          <circle className="bc-circle" cx="50" cy="50" r="46" fill="none" stroke="#1D9E75" strokeWidth="5" strokeLinecap="round" transform="rotate(-90 50 50)" />
          <path className="bc-check" d="M30 52 L44 66 L72 36" fill="none" stroke="#1D9E75" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h2 className="mt-4 text-xl font-bold tracking-tight" style={{ color: '#0D1B2A' }}>Policy Bound</h2>
        <p className="mt-2 text-3xl font-bold font-mono" style={{ color: '#1A4C7C' }} data-testid="celebrate-policy-number">{policyNumber || policyId}</p>
        {seconds != null && (
          <p className="mt-2 text-xs" style={{ color: '#5C6B7A' }} data-testid="celebrate-time">
            Policy bound and documents generated in {seconds} seconds.
          </p>
        )}
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {DOCS.map(d => (
            <a key={d.path} href={`${BACKEND_URL}/api/documents/${d.path}/${policyId}`} target="_blank" rel="noopener noreferrer"
              data-testid={d.testid}
              className="flex flex-col items-center gap-2 rounded-md px-3 py-5 text-xs font-semibold transition-all hover:-translate-y-0.5"
              style={{ border: `2px solid ${SAFFRON}`, color: '#0D1B2A', background: 'rgba(232,160,32,0.04)' }}>
              <d.icon className="w-6 h-6" style={{ color: SAFFRON }} />
              {d.label}
              <span className="text-[10px] font-normal" style={{ color: '#5C6B7A' }}>Download PDF</span>
            </a>
          ))}
        </div>
        <p className="mt-6 text-sm font-medium" style={{ color: '#1D9E75' }}>This policy is now active. Your client is covered.</p>
        <button onClick={onClose} data-testid="celebrate-continue"
          className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-md transition-colors hover:bg-[rgba(26,76,124,0.06)]"
          style={{ color: '#1A4C7C' }}>
          Continue <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>,
    document.body
  );
}
